<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      id="update-file-upload-modal"
      v-model="openModal"
      size="lg"
      title="Mise à jour du fichier"
    >
      <div class="update-file-form">
        <div class="form-row form-row-50">
          <div class="form-group col">
            <label 
              class="required"
              style="margin-top: 1em;"
            >
              {{ $t('resource.format.label') }}
            </label>
            <p>
              {{ $t('resource.format.help') }}
            </p>
            <ValidatedFileFormatSelector
              :required="true"
              validation-name="Format"
              :format-filter="
                format && format.codename ?
                  resourceDataFormats.find(el => el.codename === format.codename) :
                  null
              "
              :init-value="resourceDataFormats.find(el => el.codename === format.codename)"
              @select="$event => selectedFormat = $event"
            />
            <p style="margin-top: 0.5em; font-size: 0.85em; white-space: wrap;">
              {{ $t('resource.fileUpload.formatHelp') }}
            </p>
          </div>
        </div>

        <ImportFile
          :type="'ressource'"
          name="main-resource"
          :accept="selectedFormat ?
            resourceDataFormats
              .find(el => el.codename === selectedFormat.codename).extension
              .map(el => { return `.${el}` }).join(',') :
            ''
          "
          :required="true"
          :disabled="selectedFormat === null"
          @update="setResourceFile"
        />

        <div
          v-if="isGeo"
          class="form-row"
          style="margin-top: 1.5rem;"
        >
          <div class="form-group col-12">
            <label :class="isFieldRequired('geographiclayer__display_name', formConfig.requiredFields) ? 'required' : ''">
              {{ $t('resource.geographicalDisplayName') }}
            </label>
            <ValidationProvider
              v-slot="{ classes, errors }"
              ref="geographical_display_label"
              :rules="isFieldRequired('geographiclayer__display_name', formConfig.requiredFields)"
              :name="$t('resource.geographicalDisplayName')"
            >
              <div
                class="control"
                :class="classes"
              >
                <input
                  v-model="geoForm.geographiclayerDisplayName"
                  v-sanitize
                  class="form-control"
                  type="text"
                  :placeholder="$t('resource.geographicalDisplayName')"
                >
                <span class="form-errors">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div
          v-if="isXY"
          class="form-row"
        >
          <div class="form-group col-12">
            <label :class="isFieldRequired('column_x', formConfig.requiredFields) ? 'required' : ''">
              {{ $t('resource.XLon') }}
            </label>
            <ValidationProvider
              v-slot="{ classes, errors }"
              ref="geographical_column_x"
              :rules="isFieldRequired('column_x', formConfig.requiredFields)"
              :name="$t('resource.XLon')"
            >
              <div
                class="control"
                :class="classes"
              >
                <Multiselect
                  v-if="xyColumnsOptions && xyColumnsOptions.length"
                  v-model="geoForm.columnX"
                  :options="xyColumnsOptions.filter(el => el !== geoForm.columnY)"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  :searchable="false"
                  :placeholder="$t('resource.XLon')"
                  :disabled="file === null"
                />
                <input
                  v-else
                  v-model="geoForm.columnX"
                  v-sanitize
                  class="form-control"
                  type="text"
                  :placeholder="$t('resource.XLon')"
                  :disabled="file === null"
                >
                <span class="form-errors">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div
          v-if="isXY"
          class="form-row"
        >
          <div class="form-group col-12">
            <label :class="isFieldRequired('column_y', formConfig.requiredFields) ? 'required' : ''">
              {{ $t('resource.YLon') }}
            </label>
            <ValidationProvider
              v-slot="{ classes, errors }"
              ref="geographical_column_y"
              :rules="isFieldRequired('column_y', formConfig.requiredFields)"
              :name="$t('resource.YLon')"
            >
              <div
                class="control"
                :class="classes"
              >
                <Multiselect
                  v-if="xyColumnsOptions && xyColumnsOptions.length"
                  v-model="geoForm.columnY"
                  :options="xyColumnsOptions.filter(el => el !== geoForm.columnX)"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  :searchable="false"
                  :placeholder="$t('resource.YLon')"
                  :disabled="file === null"
                />
                <input
                  v-else
                  v-model="geoForm.columnY"
                  v-sanitize
                  class="form-control"
                  type="text"
                  :placeholder="$t('resource.YLon')"
                  :disabled="file === null"
                >
                <span class="form-errors">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div
          v-if="isXY"
          class="form-row"
        >
          <div class="form-group col-12">
            <label :class="isFieldRequired('crs', formConfig.requiredFields) ? 'required' : ''">
              {{ $t('resource.crs') }}
            </label>
            <ValidationProvider
              v-slot="{ classes, errors }"
              ref="geographical_display_label"
              :rules="isFieldRequired('crs', formConfig.requiredFields)"
              :name="$t('resource.crs')"
            >
              <div
                class="control"
                :class="classes"
              >
                <input
                  v-model="geoForm.crs"
                  v-sanitize
                  class="form-control"
                  type="text"
                  :placeholder="$t('resource.crs')"
                >
                <span class="form-errors">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="outline-secondary"
            class="float-left"
            @click="cancel"
          >
            Annuler
          </b-button>
          <b-button
            variant="primary"
            class="float-right"
            @click="handleSubmit(updateFileUploadFile)"
          >
            Confirmer
          </b-button>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapState } from 'vuex';
import { getCSVFileHeaders } from '@/utils/csv.js';

import ImportFile from '@/components/ImportFile.vue';
import Multiselect from 'vue-multiselect';
import ValidatedFileFormatSelector from '@/common/components/ValidatedFileFormatSelector.vue';
import { ValidationObserver,ValidationProvider, extend, configure } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';

extend('required', {
  ...required,
  message: 'Ce champ est requis'
});
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});

export default {
  name: 'UpdateFileUploadModal',

  components: {
    ImportFile,
    Multiselect,
    ValidatedFileFormatSelector,
    ValidationObserver,
    ValidationProvider
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    format: {
      type: Object,
      default: () => {
        return {};
      }
    },
    formConfig: {
      type: Object,
      default: () => {}
    },
  },

  data() {
    return {
      openModal: false,
      selectedFormat: null,
      file: null,
      isGeo: false,
      isXY: false,
      xyColumnsOptions: [],
      geoForm: {
        geographiclayerDisplayName: null,
        columnX: null,
        columnY: null,
        crs: null
      }
    };
  },

  computed: {
    ...mapState('resources', [
      'resourceDataFormats'
    ]),
  },

  watch: {
    open: function(newValue) {
      this.openModal = newValue;
    },
    selectedFormat: {
      deep: true,
      handler(newValue) {
        if (newValue && newValue.type.value === 'tabular-xy') {
          this.isGeo = true;
          this.isXY = true;
        } else if (newValue && newValue.type.value === 'geospatial') {
          this.isGeo = true;
          this.isXY = false;
        } else {
          this.isGeo = false;
          this.isXY = false;
        }
      }
    }
  },

  created() {
    if (this.format.type.value === 'tabular-xy') {
      this.isGeo = true;
      this.isXY = true;
    } else if (this.format.type.value === 'geospatial') {
      this.isGeo = true;
      this.isXY = false;
    } else {
      this.isGeo = false;
      this.isXY = false;
    }
  },

  methods: {
    cancel() {
      this.$emit('hide');
    },

    async setResourceFile(e) {
      const formData = new FormData();
      formData.append('file', e);
      this.file = formData;
      this.xyColumnsOptions = await getCSVFileHeaders(e);
    },

    updateFileUploadFile() {
      this.$emit('update', {
        format: this.selectedFormat,
        file: this.file,
        ...this.isGeo && {
          geographicLayer: this.geoForm
        }
      });
      this.$emit('hide');
    }
  }
};
</script>

<style lang="less" scoped>
h2 {
  color: @blue;
  margin-top: 0.8em;
  margin-left: 0.5em;
}

.update-file-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  .form-row {
    width: 80%;
  }
  .form-row-50 {
    width: 50%;
    margin: auto;
  }
}

p {
  width: 100%;
  color: grey;
  font-style: italic;
  font-size: 0.7em;
  margin-bottom: 0.2em;
}

</style>
